import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ProjectsByLpaService } from './projects-by-lpa.service';
import { ChartData, FilterOptions, ProjectsByLpaData } from '../../models/projects-by-lpa.models';
import { NgbCalendar } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-projects-by-lpa',
    templateUrl: './projects-by-lpa.component.html',
    styleUrls: ['./projects-by-lpa.component.scss'],
    standalone: false
})
export class ProjectsByLpaComponent implements OnInit, AfterViewInit {
  @ViewChild('chartContainer', { static: false }) chartContainer!: ElementRef;
  
  // Input property to control filter visibility from parent component
  @Input() isFilterVisible: boolean = false;

  // Chart options - optimized for better space usage
  view: any[] = [700, 350];
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  xAxisLabel = 'Project Count';
  showYAxisLabel = true;
  yAxisLabel = 'LPC';
  animations = true;
  legendPosition = 'bottom';
  
  // Color scheme for the chart - map specific colors to specific categories
  colorScheme = {
    name: 'customScheme',
    selectable: true,
    group: 'Ordinal',
    domain: [
      '#1E90FF', // Blue for In Progress
      '#FFA500', // Orange for Pending Payment
      '#DC143C', // Red for Rejected
      '#32CD32', // Green for Submitted to Billing
      '#FFD700', // Gold for WAITING
      '#808080'  // Gray for any other status
    ]
  };

  // Define consistent color mapping for project statuses
  private statusColorMap: { [key: string]: string } = {
    'In Progress': '#1E90FF',        // Blue
    'Pending Payment': '#FFA500',    // Orange
    'Rejected': '#DC143C',           // Red
    'Submitted to Billing': '#32CD32', // Green
    'WAITING': '#FFD700',            // Gold
    'Completed': '#228B22',          // Forest Green
    'On Hold': '#FF6347',            // Tomato
    'Cancelled': '#A9A9A9'           // Dark Gray
  };

  // Custom color function for ngx-charts
  customColors = (value: string): string => {
    return this.statusColorMap[value] || '#808080'; // Default to gray if status not found
  };

  // Chart data
  chartData: ChartData[] = [];
  originalData: ProjectsByLpaData[] = [];
  
  // Map to store user email addresses by display name for navigation
  userEmailMap: Map<string, string> = new Map();
  
  // Filter options
  projectTypeOptions: any[] = [];
  
  // Selected filter values
  selectedProjectType: any;
  
  // Filter visibility - always true as it's controlled by parent through isFilterVisible
  showFilters: boolean = true;
  
  // UI state
  isLoading: boolean = false;
  error: string | null = null;
  
  // X-axis tick formatting function to display whole numbers only (for the horizontal bars, X-axis shows the counts)
  xAxisTickFormatting = (value: number) => {
    return Math.round(value).toString();
  };
  
  constructor(
    private projectsByLpaService: ProjectsByLpaService,
    private calendar: NgbCalendar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchFilterOptions();
    this.loadData();
  }
  
  ngAfterViewInit(): void {
    // Initialize chart size after view is initialized
    setTimeout(() => {
      this.updateChartSize();
    }, 100);
  }
  
  /**
   * Fetch filter options from API
   */
  fetchFilterOptions(): void {
    this.isLoading = true;
    
    // Fetch project types
    this.projectsByLpaService.getProjectTypes()
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.projectTypeOptions = response.data;
            console.log('Project types loaded:', this.projectTypeOptions);
          } else {
            console.warn('No project type data received');
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading project types:', error);
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Load chart data from API
   */
  loadData(): void {
    this.isLoading = true;
    this.error = null;
    
    // Prepare filter options (only project type)
    const filters: FilterOptions = {
      projectType: this.selectedProjectType || undefined
    };
    
    this.projectsByLpaService.getProjectsByLpaData(filters)
      .subscribe({
        next: (response: any) => {
          if (response && response.data) {
            this.originalData = response.data;
            this.processChartData(response.data);
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading projects by LPC data:', err);
          this.error = 'Failed to load projects by LPC data';
          this.isLoading = false;
        }
      });
  }
  
  /**
   * Process API data into chart format
   * API already groups by userId, we just need to reorganize by userId to stack different statuses
   */
  processChartData(data: ProjectsByLpaData[]): void {
    console.log('Raw API data:', data);
    
    // Group by userId to collect all statuses for each user
    const groupedByUser: Map<number, { userName: string, userMainEmail: string, series: Array<{ name: string, value: number }> }> = new Map();
    
    data.forEach(item => {
      const userId = item.userId;
      
      // Handle null, undefined, or empty userName
      let userName = item.userName;
      if (!userName || userName.trim() === '' || userName === 'null' || userName === 'undefined') {
        userName = 'Unknown LPC';
      }
      
      const userMainEmail = item.userMainEmail || '';
      const projectStatus = item.projCategory || 'Unknown Status';
      const count = Math.round(item.taskCount || 0);
      
      // Initialize user if not exists
      if (!groupedByUser.has(userId)) {
        groupedByUser.set(userId, {
          userName: userName,
          userMainEmail: userMainEmail,
          series: []
        });
      }
      
      // Add this status to the user's series
      groupedByUser.get(userId)!.series.push({
        name: projectStatus,
        value: count
      });
    });
    
    // Check for duplicate userNames to append userId for uniqueness
    const userNameCounts = new Map<string, number>();
    groupedByUser.forEach(userData => {
      const count = userNameCounts.get(userData.userName) || 0;
      userNameCounts.set(userData.userName, count + 1);
    });
    
    // Store mapping of display names to user emails for navigation
    this.userEmailMap.clear();
    
    // Convert to chart format
    this.chartData = Array.from(groupedByUser.entries()).map(([userId, userData]) => {
      // If this userName appears more than once, append userId to make it unique
      let displayName = userData.userName;
      if (userNameCounts.get(userData.userName)! > 1) {
        displayName = `${userData.userName} (${userId})`;
      }
      
      // Store the email mapping for this display name
      this.userEmailMap.set(displayName, userData.userMainEmail);
      
      return {
        name: displayName,
        series: userData.series
      };
    });
    
    console.log('Chart data processed:', this.chartData);
    console.log('User email mapping:', this.userEmailMap);
    
    // Update chart size based on new data
    setTimeout(() => this.updateChartSize(), 0);
  }
  
  /**
   * Format date to MM/DD/YY
   */
  formatDate(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear().toString().substr(2); // Get last 2 digits of year
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${month}/${day}/${year}`;
  }
  
  /**
   * Apply all filters and reload data
   */
  applyAllFilters(): void {
    this.loadData();
  }
  
  /**
   * Clear all filters and reload data
   */
  clearAllFilters(): void {
    this.selectedProjectType = null;
    this.loadData();
  }
  
  /**
   * Handle bar click event - navigate to projects grid
   */
  onSelect(event: any): void {
    if (!event || !event.name) return;
    
    // Extract LPC name and project status from the clicked bar
    // event.name = project status ("In Progress", "Pending Payment", etc.)
    // event.series = LPC name (from the Y-axis)
    const lpcName = event.series;
    const projectStatus = event.name;
    
    // Get the user email from the map
    const lpcMainEmail = this.userEmailMap.get(lpcName) || '';

    console.log('Bar clicked:', { lpcName, projectStatus, lpcMainEmail });

    // Prepare filters array for the projects grid
    const filters: any[] = [
      {
        field: 'statusCode',
        operator: 'in',
        values: [projectStatus]
      }
    ];

    // Add lpcUserEmail filter if available
    if (lpcMainEmail) {
      filters.push({
        field: 'lpcUserEmail',
        operator: 'equals',
        value: lpcMainEmail
      });
    }

    // Navigate to projects grid with navigation state containing filters
    this.router.navigate(['/projects'], {
      state: {
        filters: filters,
        category: 'All Projects',
        fromWidget: 'projects-by-lpa'
      }
    });
  }
  
  /**
   * Handle project type selection change
   */
  onProjectTypeChange(event: any): void {
    // Project type change handling - can trigger reload if needed
  }
  
  /**
   * Update chart size on window resize
   */
  @HostListener('window:resize')
  onResize(): void {
    this.updateChartSize();
  }
  
  /**
   * Update chart size based on container dimensions and data content
   */
  private updateChartSize(): void {
    if (this.chartContainer) {
      const container = this.chartContainer.nativeElement;
      const width = container.offsetWidth;
      
      // Calculate height based on number of data items
      // Each bar needs ~60-80px, plus space for axis labels and legend
      const itemCount = this.chartData.length || 1;
      const heightPerItem = 60;
      const baseHeight = 100; // For axis and legend
      const calculatedHeight = (itemCount * heightPerItem) + baseHeight;
      
      // Set reasonable min and max bounds
      const chartHeight = Math.max(280, Math.min(calculatedHeight, 500));
      
      this.view = [width, chartHeight];
    }
  }
}
